import { db } from '../firebase';
import { doc, updateDoc, getDoc, increment, setDoc, arrayUnion } from 'firebase/firestore';
import { recordImageProcessingHistory } from './imageMetricsService';

const GLOBAL_STATS_ID = 'global_statistics';
const GLOBAL_METRICS_COLLECTION = 'globalMetrics';
const USER_METRICS_COLLECTION = 'userMetrics';

export const updateStatistics = async (shopId, userId, stats) => {
  try {
    // Ensure all values are numbers and not undefined
    const imagesProcessed = parseInt(stats.imagesProcessed) || 0;
    const bytesOriginal = parseInt(stats.bytesOriginal) || 0;
    const bytesProcessed = parseInt(stats.bytesProcessed) || 0;
    const bytesSaved = parseInt(stats.bytesSaved) || 0;
    
    // Convert to MB for storage (ensure it's a number)
    const bytesSavedMB = bytesSaved / (1024 * 1024);
    const bytesOriginalMB = bytesOriginal / (1024 * 1024);
    const bytesProcessedMB = bytesProcessed / (1024 * 1024);

    // Log the incoming stats with raw values for debugging
    console.log('Processing Statistics Update:', {
      shopId,
      userId,
      rawStats: stats,
      processedStats: {
        imagesProcessed,
        bytesOriginal,
        bytesProcessed,
        bytesSaved,
        bytesSavedMB: bytesSavedMB.toFixed(2) + ' MB'
      }
    });

    // Calculate compression ratio based on original and processed sizes
    const compressionRatio = bytesOriginal ? 
      (((bytesOriginal - bytesProcessed) / bytesOriginal) * 100).toFixed(2) : 0;

    // Record daily image processing history
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    await recordImageProcessingHistory(
      userId,
      imagesProcessed,
      bytesOriginalMB,
      bytesProcessedMB,
      bytesSavedMB,
      compressionRatio,
      today
    );

    // 1. Update shop metrics
    const shopRef = doc(db, 'shops', shopId);
    const shopDoc = await getDoc(shopRef);

    if (!shopDoc.exists()) {
      console.error('Shop document not found');
      return false;
    }

    const shopData = shopDoc.data();
    const currentShopMetrics = shopData.metrics || {};

    // Log current shop metrics before update with raw values
    console.log('Current Shop Metrics:', {
      totalImagesProcessed: currentShopMetrics.totalImagesProcessed || 0,
      totalBytesSaved: currentShopMetrics.totalBytesSaved || 0,
      totalBytesSavedMB: (currentShopMetrics.totalBytesSavedMB || 0).toFixed(2) + ' MB',
      formatted: {
        totalBytesSaved: formatBytes(currentShopMetrics.totalBytesSaved || 0)
      }
    });

    // Get current date for monthly and daily stats
    const currentDate = new Date();
    const monthKey = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}`;
    const dayKey = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}-${currentDate.getDate().toString().padStart(2, '0')}`;
    
    // Calculate compression ratio based on original and processed sizes
    const calculatedCompressionRatio = bytesOriginal ? 
      (((bytesOriginal - bytesProcessed) / bytesOriginal) * 100).toFixed(2) : 0;

    // Update with the processed numeric values
    await updateDoc(shopRef, {
      'metrics.totalImagesProcessed': increment(imagesProcessed),
      'metrics.totalBytesSaved': increment(bytesSaved),
      'metrics.totalBytesSavedMB': increment(bytesSavedMB),
      'metrics.averageCompressionRatio': calculatedCompressionRatio,
      'metrics.lastImageUpdate': new Date().toISOString(),
      // Add monthly and daily stats
      [`metrics.monthlyImageStats.${monthKey}.imagesProcessed`]: increment(imagesProcessed),
      [`metrics.monthlyImageStats.${monthKey}.bytesSaved`]: increment(bytesSaved),
      [`metrics.monthlyImageStats.${monthKey}.bytesSavedMB`]: increment(bytesSavedMB),
      [`metrics.dailyImageStats.${dayKey}.imagesProcessed`]: increment(imagesProcessed),
      [`metrics.dailyImageStats.${dayKey}.bytesSaved`]: increment(bytesSaved),
      [`metrics.dailyImageStats.${dayKey}.bytesSavedMB`]: increment(bytesSavedMB)
    });

    // Log shop metrics update with raw values
    console.log('Updated Shop Metrics:', {
      totalImagesProcessed: `+${imagesProcessed}`,
      totalBytesSaved: bytesSaved,
      formatted: {
        totalBytesSaved: formatBytes(bytesSaved)
      },
      totalBytesSavedMB: `${bytesSavedMB.toFixed(2)} MB`,
      monthKey,
      dayKey
    });

    // 2. Update user metrics
    const userRef = doc(db, USER_METRICS_COLLECTION, userId);
    const userDoc = await getDoc(userRef);
    const now = new Date().toISOString();

    if (!userDoc.exists()) {
      // Initialize user metrics document
      const initialUserMetrics = {
        totalImagesProcessed: imagesProcessed,
        totalBytesSaved: bytesSaved,
        totalBytesSavedMB: bytesSavedMB,
        averageCompressionRatio: calculatedCompressionRatio,
        lastUpdated: now,
        shops: [shopId],
        totalShopsProcessed: 1
      };

      console.log('Creating New User Metrics:', initialUserMetrics);
      // Use setDoc instead of updateDoc for a new document
      await setDoc(userRef, initialUserMetrics);
    } else {
      const userData = userDoc.data();
      
      // Log current user metrics
      console.log('Current User Metrics:', {
        totalImagesProcessed: userData.totalImagesProcessed || 0,
        totalBytesSaved: userData.totalBytesSaved || 0,
        totalBytesSavedMB: (userData.totalBytesSavedMB || 0).toFixed(2) + ' MB',
        formatted: {
          totalBytesSaved: formatBytes(userData.totalBytesSaved || 0)
        }
      });

      await updateDoc(userRef, {
        totalImagesProcessed: increment(imagesProcessed),
        totalBytesSaved: increment(bytesSaved),
        totalBytesSavedMB: increment(bytesSavedMB),
        averageCompressionRatio: calculatedCompressionRatio,
        lastUpdated: now,
        shops: arrayUnion(shopId),
        totalShopsProcessed: userData.shops?.includes(shopId) ? 
          userData.totalShopsProcessed || 1 : 
          increment(1)
      });

      // Log user metrics update
      console.log('Updated User Metrics:', {
        totalImagesProcessed: `+${imagesProcessed}`,
        totalBytesSaved: bytesSaved,
        formatted: {
          totalBytesSaved: formatBytes(bytesSaved)
        },
        totalBytesSavedMB: `${bytesSavedMB.toFixed(2)} MB`
      });
    }

    // 3. Update global metrics
    const globalRef = doc(db, GLOBAL_METRICS_COLLECTION, GLOBAL_STATS_ID);
    const globalDoc = await getDoc(globalRef);

    if (!globalDoc.exists()) {
      // Initialize global metrics document
      const initialGlobalMetrics = {
        totalImagesProcessed: imagesProcessed,
        totalBytesSaved: bytesSaved,
        totalBytesSavedMB: bytesSavedMB,
        totalShopsProcessed: 1,
        totalUsersProcessed: 1,
        averageCompressionRatio: calculatedCompressionRatio,
        lastUpdated: now
      };

      await setDoc(globalRef, initialGlobalMetrics);
      console.log('Created New Global Metrics:', initialGlobalMetrics);
    } else {
      const globalData = globalDoc.data();
      
      // Log current global metrics
      console.log('Current Global Metrics:', {
        totalImagesProcessed: globalData.totalImagesProcessed || 0,
        totalBytesSaved: globalData.totalBytesSaved || 0,
        totalBytesSavedMB: (globalData.totalBytesSavedMB || 0).toFixed(2) + ' MB',
        formatted: {
          totalBytesSaved: formatBytes(globalData.totalBytesSaved || 0)
        }
      });

      await updateDoc(globalRef, {
        totalImagesProcessed: increment(imagesProcessed),
        totalBytesSaved: increment(bytesSaved),
        totalBytesSavedMB: increment(bytesSavedMB),
        averageCompressionRatio: calculatedCompressionRatio,
        lastUpdated: now
      });

      // Log global metrics update
      console.log('Updated Global Metrics:', {
        totalImagesProcessed: `+${imagesProcessed}`,
        totalBytesSaved: bytesSaved,
        formatted: {
          totalBytesSaved: formatBytes(bytesSaved)
        },
        totalBytesSavedMB: `${bytesSavedMB.toFixed(2)} MB`
      });
    }

    console.log('All Statistics Updated Successfully');
    return true;
  } catch (error) {
    console.error('Error updating statistics:', error);
    throw error;
  }
};

// Helper functions to get statistics
export const getShopStatistics = async (shopId) => {
  try {
    const shopRef = doc(db, 'shops', shopId);
    const shopDoc = await getDoc(shopRef);
    return shopDoc.exists() ? shopDoc.data().metrics : null;
  } catch (error) {
    console.error('Error getting shop statistics:', error);
    throw error;
  }
};

export const getUserStatistics = async (userId) => {
  try {
    const userRef = doc(db, USER_METRICS_COLLECTION, userId);
    const userDoc = await getDoc(userRef);
    return userDoc.exists() ? userDoc.data() : null;
  } catch (error) {
    console.error('Error getting user statistics:', error);
    throw error;
  }
};

export const getGlobalStatistics = async () => {
  try {
    const globalRef = doc(db, GLOBAL_METRICS_COLLECTION, GLOBAL_STATS_ID);
    const globalDoc = await getDoc(globalRef);
    return globalDoc.exists() ? globalDoc.data() : null;
  } catch (error) {
    console.error('Error getting global statistics:', error);
    throw error;
  }
};

export const formatBytes = (bytes, decimals = 2) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};
